import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, DollarSign, MapPin, Check, Share2, ExternalLink, Compass, Store, Route } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Overline, TourCard, img } from "@/components/Cards";
import { toast } from "sonner";

export default function TourDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setT(null); setNotFound(false); setActive(0);
    api.get(`/tours/${slug}`).then((r) => setT(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-400">Tour not found. <Link to="/tours" className="text-[#00E5FF]">Back to tours</Link></div>;
  if (!t) return <div className="py-32 text-center text-neutral-500">Loading...</div>;

  const gallery = (t.gallery && t.gallery.length ? t.gallery : [t.image]).filter(Boolean);
  const host = t.host;
  const hostLink = host ? (host.kind === "operator" ? `/operators/${host.slug}` : `/directory/${host.slug}`) : null;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: `${t.title} — Visit RiNo`, url }); } catch {} }
    else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  const jsonLd = {
    "@context": "https://schema.org", "@type": "TouristAttraction",
    name: t.title, description: t.description, ...(t.image ? { image: [t.image] } : {}),
    ...(t.price ? { offers: { "@type": "Offer", price: t.price, priceCurrency: "USD" } } : {}),
  };

  return (
    <div data-testid="tour-detail">
      <Seo title={t.title} description={t.description?.slice(0, 155)} image={t.image} type="article" jsonLd={jsonLd} canonicalPath={`/tours/${t.slug}`} />

      <div className="relative h-[48vh] overflow-hidden grain">
        <img src={img(gallery[active])} alt={t.title} className="w-full h-full object-cover transition-all duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
            <Link to="/tours" className="flex w-fit items-center gap-2 text-neutral-300 hover:text-white text-sm mb-4" data-testid="back-to-tours"><ArrowLeft size={16} /> Tours</Link>
            <div><Overline>{t.category || "Tour"}</Overline></div>
            <h1 className="font-display font-black text-4xl sm:text-6xl text-white mt-2" data-testid="tour-title">{t.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-neutral-200 text-sm mt-3">
              {t.duration && <span className="flex items-center gap-2"><Clock size={16} className="text-[#00E5FF]" /> {t.duration}</span>}
              {t.price && <span className="flex items-center gap-2 text-[#FFEA00]"><DollarSign size={16} /> {t.price}</span>}
              {t.operator && <span className="flex items-center gap-2"><Compass size={16} className="text-[#00E5FF]" /> {t.operator}</span>}
            </div>
          </div>
        </div>
      </div>

      {gallery.length > 1 && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-4 flex gap-2 overflow-x-auto" data-testid="tour-gallery">
          {gallery.map((g, i) => (
            <button key={i} onClick={() => setActive(i)} className={`w-24 h-16 flex-shrink-0 overflow-hidden border-2 transition-colors ${active === i ? "border-[#00E5FF]" : "border-transparent opacity-70 hover:opacity-100"}`}>
              <img src={img(g)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <h2 className="font-display font-bold text-2xl text-white mb-3">About this experience</h2>
            <p className="text-neutral-300 text-lg leading-relaxed whitespace-pre-line" data-testid="tour-description">{t.description || "Details coming soon."}</p>
          </div>

          {t.highlights?.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-4">Highlights</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tour-highlights">
                {t.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-neutral-300 text-sm"><Check size={16} className="text-[#00FF66] mt-0.5 flex-shrink-0" /> {h}</li>
                ))}
              </ul>
            </div>
          )}

          {t.itinerary?.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-4 flex items-center gap-2"><Route size={20} className="text-[#00E5FF]" /> Itinerary</h2>
              <ol className="relative border-l border-white/15 ml-3 space-y-6" data-testid="tour-itinerary">
                {t.itinerary.map((step, i) => (
                  <li key={i} className="ml-6">
                    <span className="absolute -left-3 flex items-center justify-center w-6 h-6 bg-[#00E5FF] text-black text-xs font-bold rounded-full">{i + 1}</span>
                    <p className="text-neutral-300 text-sm leading-relaxed">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {t.included && (
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-3">What's included</h2>
              <p className="text-neutral-300 text-base leading-relaxed whitespace-pre-line">{t.included}</p>
            </div>
          )}

          {t.more_tours?.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-4">More RiNo tours</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="more-tours">
                {t.more_tours.map((mt, i) => <TourCard key={mt.slug} t={mt} index={i} />)}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-32 border border-white/10 bg-[#141414] p-6 space-y-4">
            <div className="flex items-baseline justify-between">
              <span className="text-neutral-400 text-sm">From</span>
              <span className="font-display font-black text-3xl text-[#FFEA00]">{t.price || "—"}</span>
            </div>
            {t.duration && <div className="flex items-center gap-2 text-neutral-300 text-sm"><Clock size={15} className="text-[#00E5FF]" /> {t.duration}</div>}
            {t.meeting_point && <div className="flex items-start gap-2 text-neutral-300 text-sm"><MapPin size={15} className="text-[#00E5FF] mt-0.5" /> {t.meeting_point}</div>}
            {t.booking_url ? (
              <a href={t.booking_url} target="_blank" rel="noopener noreferrer" data-testid="tour-book-btn"
                className="w-full py-3 bg-[#FF0055] hover:bg-[#ff2d74] text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors">
                Book Now <ExternalLink size={15} />
              </a>
            ) : (
              <div className="w-full py-3 bg-white/5 text-neutral-400 text-sm text-center">Booking info coming soon</div>
            )}
            <button onClick={share} data-testid="tour-share-btn"
              className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
              <Share2 size={16} /> Share
            </button>

            {host && (
              <Link to={hostLink} data-testid="tour-host-link" className="flex items-center gap-3 border-t border-white/10 pt-4 mt-2 group">
                {host.logo ? <img src={host.logo} alt={host.name} className="w-11 h-11 object-cover flex-shrink-0" /> : <div className="w-11 h-11 bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">{host.kind === "operator" ? <Compass size={18} className="text-[#00E5FF]" /> : <Store size={18} className="text-[#00E5FF]" />}</div>}
                <div className="min-w-0">
                  <div className="text-neutral-500 text-xs">Hosted by</div>
                  <div className="text-white text-sm font-medium truncate group-hover:text-[#00E5FF] transition-colors">{host.name}</div>
                </div>
              </Link>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
